// @ts-nocheck
/*
 * Port of views/dashboard/numbers/edit.js (262 lines).
 * BYTE-IDENTICAL — GET pre-populates from API_NUMBER_EDIT, cost is
 * mandatory unless Free category, VMN/TOLL FREE require linkedDidId.
 */
import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card as ShadcnCard } from '@/components/ui/card';
import { ScrollPage } from '@/components/layout/PageContainer';
import { PageHeader } from '@/components/feedback/PageHeader';
import { toast } from 'sonner';
import { friendlyApiError } from '@/utilities/apiErrorMessage';
import { postApiCall, getApiCall } from '@/service/httpCall';
import { API_NUMBER_EDIT } from '@/service/url';
import { useNavigate, useParams } from 'react-router-dom';
import { ROUTES } from '@/utilities/constant';
import RenderNumberForm from '@/features/numbers/NumberFormUpdate';
import { useAuth, errorCheck } from '@/views/dashboard/auth/auth-utils';

function getApiUrl(url, id) {
  return url.replace(':id', id);
}

export default function NumbersEdit() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { checkAuth } = useAuth();
  const [DID, setDID] = useState('');
  const [countryCodeId, setCountryCodeId] = useState('');
  const [stateCode, setStateCode] = useState('');
  const [numberType, setNumberType] = useState('');
  const [category, setCategory] = useState('');
  const [currency, setCurrency] = useState('');
  const [cost, setCost] = useState('');
  const [operator, setOperator] = useState('');
  const [status, setStatus] = useState('');
  const [trunks, setTrunks] = useState('');
  const [numberRoutingType, setNumberRoutingType] = useState('');
  const [linkedDidId, setLinkedDidId] = useState('');

  const [showToast, setShowToast] = useState(false);
  const [toastProps, setToastProps] = useState(null);
  const [buttonDisable, setButtonDisable] = useState(false);

  useEffect(() => {
    checkAuth(setToastProps, setShowToast);
  }, []);

  useEffect(() => {
    const editApiUrl = getApiUrl(API_NUMBER_EDIT, id);
    getApiCall(editApiUrl, {}).then(onGetSuccess).catch(onError);
  }, [id]);

  function onGetSuccess(resp) {
    setDID(resp.actual_number || '');
    setStateCode(resp.state_code || '');
    setCountryCodeId(resp.country_code || '');
    setCost(resp.cost || '');
    setNumberType(resp.number_type || '');
    setCategory(resp.category || '');
    setCurrency(resp.currency || '');
    setOperator(resp.operator || '');
    setStatus(resp.status || '');
    setTrunks(resp.trunk_id || '');
    setNumberRoutingType(resp.number_routing_type || '');
    setLinkedDidId(resp.linked_did_id ? String(resp.linked_did_id) : '');
  }

  function validateForm() {
    let valid = true;
    if (!countryCodeId) {
      valid = false;
      toast.error('Country Code required');
    } else if (!numberType) {
      valid = false;
      toast.error('Number Type is required');
    } else if (numberType === 'DID' && !category) {
      valid = false;
      toast.error('Category is required for DID');
    } else if (!currency) {
      valid = false;
      toast.error('Currency is required');
    } else if (numberType === 'DID' && !trunks) {
      valid = false;
      toast.error('Trunk is required');
    } else if (!DID) {
      valid = false;
      toast.error('DID is required');
    } else if (!cost && category !== 'Free') {
      valid = false;
      toast.error('Cost is required');
    } else if (!operator) {
      valid = false;
      toast.error('Operator is required');
    } else if (numberType === 'DID' && !stateCode) {
      valid = false;
      toast.error('State Code is required for DID');
    } else if (!status) {
      valid = false;
      toast.error('Status is required');
    } else if (!numberRoutingType) {
      valid = false;
      toast.error('Number Routing Type is required');
    } else if ((numberType === 'VMN' || numberType === 'TOLL FREE') && !linkedDidId) {
      valid = false;
      toast.error('DID Number is required for VMN/TOLL FREE');
    }
    return valid;
  }

  async function submitForm(e) {
    e.preventDefault();
    setButtonDisable(true);

    if (!validateForm()) {
      setButtonDisable(false);
      return;
    }

    let params: any = {
      numberType,
      currency,
      countryCode: countryCodeId,
      DID,
      cost: category === 'Free' ? '0' : cost,
      operator,
      status,
      number_routing_type: numberRoutingType,
    };

    if (numberType === 'DID') {
      params.category = category;
      params.stateCode = stateCode;
      params.trunk_id = trunks;
    }
    if (numberType === 'VMN' || numberType === 'TOLL FREE') {
      params.linkedDidId = linkedDidId;
    }

    const editApiUrl = getApiUrl(API_NUMBER_EDIT, id);
    postApiCall(editApiUrl, params).then(onNumberUpdateSuccess).catch(onError);
  }

  function onNumberUpdateSuccess() {
    toast.success('Number updated successfully');
    setTimeout(() => {
      navigate(`/${ROUTES.NUMBERS.LIST}`);
    }, 1500);
  }

  function onError(err) {
    if (typeof err !== 'object' && err.includes('TokenExpiredError')) {
      errorCheck(err, navigate, setToastProps, setShowToast);
    } else {
      setButtonDisable(false);
      toast.error(friendlyApiError(err) || 'Failed to update number');
    }
  }

  return (
    <ScrollPage>
      <PageHeader
        title="Update Number"
        description="Adjust the DID / VMN entry and its inbound-routing metadata."
      />
      <div className="row">
        <div className="col-12">
          <ShadcnCard>
            <div className="p-4">
              <form onSubmit={async (e) => { submitForm(e); }}>
                <RenderNumberForm
                  DID={DID}
                  setDID={setDID}
                  countryCodeId={countryCodeId}
                  setCountryCodeId={setCountryCodeId}
                  stateCode={stateCode}
                  setStateCode={setStateCode}
                  cost={cost}
                  setCost={setCost}
                  numberType={numberType}
                  setNumberType={setNumberType}
                  category={category}
                  setCategory={setCategory}
                  currency={currency}
                  setCurrency={setCurrency}
                  operator={operator}
                  setOperator={setOperator}
                  status={status}
                  setStatus={setStatus}
                  buttonDisable={buttonDisable}
                  setButtonDisable={setButtonDisable}
                  toastProps={toastProps}
                  setToastProps={setToastProps}
                  trunks={trunks}
                  setTrunks={setTrunks}
                  numberRoutingType={numberRoutingType}
                  setNumberRoutingType={setNumberRoutingType}
                  linkedDidId={linkedDidId}
                  setLinkedDidId={setLinkedDidId}
                />

                <Button type={'submit'} disabled={buttonDisable}>
                  Update Number &nbsp;
                  {buttonDisable ? (
                    <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />
                  ) : (
                    <></>
                  )}
                </Button>
              </form>
            </div>
          </ShadcnCard>
        </div>
      </div>
    </ScrollPage>
  );
}
